import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Copy, Pencil, Trash2, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ChecklistRunner from "@/components/playbooks/ChecklistRunner";
import { AiFeedback, ApplyClimb } from "@/components/playbooks/PremiumTools";

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      <div className="mt-1 font-mono font-bold text-lg text-slate-900 tabular-nums" data-testid={testId}>{value ?? "—"}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest font-semibold text-blue-800 mb-2">{title}</div>
      {children}
    </div>
  );
}

function PremiumOnly({ children }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-4 text-sm text-amber-900 flex items-start gap-3" data-testid="playbook-premium-only">
      <Lock size={16} className="mt-0.5 shrink-0" /><span>{children || t("playbooks.premiumToolsLocked")}</span>
    </div>
  );
}

export default function PlaybookDetail({ id, premium, isAdmin, onEdit, onChanged }) {
  const { t, i18n } = useTranslation();
  const [p, setP] = useState(null);
  const load = useCallback(() => api.get(`/playbooks/${id}`, { params: { lang: i18n.language } }).then((r) => setP(r.data)).catch(() => setP(null)), [id, i18n.language]);
  useEffect(() => { load(); }, [load]);
  if (!p) return null;
  const s = p.stats || {};
  const canEdit = (p.mine && premium) || (p.is_template && isAdmin);
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChanged?.(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  const r = (v) => (v == null ? null : `${v > 0 ? "+" : ""}${v}R`);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-7" data-testid="playbook-detail">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{p.market || "—"}</div>
          <h2 className="mt-1 font-display font-black text-2xl text-slate-900" data-testid="playbook-detail-title">{p.title}</h2>
          {p.description && <p className="mt-2 text-sm text-slate-600 max-w-2xl">{p.description}</p>}
        </div>
        <div className="flex gap-2">
          {premium && p.is_template && <Button variant="outline" className="gap-2" data-testid="playbook-copy-btn"
            onClick={() => act(() => api.post(`/playbooks/${p.id}/copy`, null, { params: { lang: i18n.language } }), t("playbooks.copied"))}><Copy size={14} />{t("playbooks.copy")}</Button>}
          {canEdit && <Button variant="outline" className="gap-2" data-testid="playbook-edit-btn" onClick={() => onEdit(p)}><Pencil size={14} />{t("playbooks.edit")}</Button>}
          {canEdit && <Button variant="outline" className="gap-2 text-red-700 border-red-200" data-testid="playbook-delete-btn"
            onClick={() => window.confirm(t("playbooks.confirmDelete")) && act(() => api.delete(`/playbooks/${p.id}`), t("playbooks.deleted"))}><Trash2 size={14} /></Button>}
        </div>
      </div>
      {!premium ? <PremiumOnly>{t("playbooks.statsLocked")}</PremiumOnly> : <>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label={t("playbooks.s.trades")} value={s.trades} testId="playbook-stat-trades" />
        <Stat label={t("playbooks.s.winRate")} value={s.win_rate != null ? `${s.win_rate}%` : null} testId="playbook-stat-winrate" />
        <Stat label={t("playbooks.s.netR")} value={r(s.net_r)} testId="playbook-stat-netr" />
        <Stat label={t("playbooks.s.followed")} value={s.followed_rate != null ? `${s.followed_rate}%` : null} testId="playbook-stat-followed" />
        <Stat label={t("playbooks.s.checks")} value={s.checks_run} testId="playbook-stat-checks" />
      </div>
      {s.trades === 0 && <p className="text-xs text-slate-500 -mt-4">{t("playbooks.linkHint")}</p>}
      </>}
      <div className="grid lg:grid-cols-2 gap-8">
        <Section title={t("playbooks.preTrade")}><ChecklistRunner playbook={p} onRun={load} /></Section>
        <div className="space-y-6">
          {p.setup_rules?.length > 0 && <Section title={t("playbooks.f.setup_rules")}><ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">{p.setup_rules.map((x, i) => <li key={i}>{x}</li>)}</ul></Section>}
          {["entry_rules", "exit_rules", "risk_rule"].filter((k) => p[k]).map((k) => (
            <Section key={k} title={t(`playbooks.f.${k}`)}><p className="text-sm text-slate-700">{p[k]}</p></Section>
          ))}
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6 pt-2 border-t border-slate-100">
        <Section title={t("playbooks.professorTitle")}>{premium ? <AiFeedback playbook={p} /> : <PremiumOnly />}</Section>
        <Section title={t("playbooks.climbTitle")}>{premium ? <ApplyClimb playbook={p} /> : <PremiumOnly />}</Section>
      </div>
    </div>
  );
}
